import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import { MapContainer, TileLayer, CircleMarker, Marker, Polygon, Polyline, LayersControl, useMap, useMapEvents, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Crosshair, Undo2, Trash2, MapPin, Spline, Search, Eraser, Loader2, Hand } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const DEFAULT_CENTER = [-0.95, 100.35];
const MAX_ZOOM = 22;
export const STATE_COLORS = {
  FREE: ["#16a34a", "HIJAU · FREE / TERSEDIA"], BOOKED: ["#eab308", "KUNING · BOOKING"], INVESTED: ["#2563eb", "BIRU · TERINVESTASI"],
  PARTIAL: ["#9333ea", "UNGU · SEBAGIAN TERISI"], INACTIVE: ["#9ca3af", "ABU-ABU · NONAKTIF"], COMPLETED: ["#6b7280", "ABU-ABU · KONTRAK SELESAI"],
};
export const EXCLUSION_COLOR = "#dc2626";

export const polygonHectares = (pts) => {
  if (!pts || pts.length < 3) return 0;
  const lat0 = pts.reduce((s, p) => s + p[0], 0) / pts.length;
  const k = Math.cos((lat0 * Math.PI) / 180);
  const xy = pts.map(([lat, lng]) => [lng * 111320 * k, lat * 110540]);
  let a = 0;
  for (let i = 0; i < xy.length; i += 1) {
    const [x1, y1] = xy[i];
    const [x2, y2] = xy[(i + 1) % xy.length];
    a += x1 * y2 - x2 * y1;
  }
  return Math.abs(a) / 2 / 10000;
};
export const fmtArea = (ha) => `${Math.round(ha * 10000).toLocaleString("id-ID")} m² · ${ha.toLocaleString("id-ID", { maximumFractionDigits: 4 })} ha`;

const cross = (a, b, c) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
const segHit = (p1, p2, p3, p4) => {
  const d1 = cross(p3, p4, p1), d2 = cross(p3, p4, p2), d3 = cross(p1, p2, p3), d4 = cross(p1, p2, p4);
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) return true;
  const on = (a, b, c) => Math.min(a[0], b[0]) <= c[0] && c[0] <= Math.max(a[0], b[0]) && Math.min(a[1], b[1]) <= c[1] && c[1] <= Math.max(a[1], b[1]);
  return (d1 === 0 && on(p3, p4, p1)) || (d2 === 0 && on(p3, p4, p2)) || (d3 === 0 && on(p1, p2, p3)) || (d4 === 0 && on(p1, p2, p4));
};
export const SELF_X_MSG = "Batas lahan saling berpotongan. Silakan perbaiki titik batas.";
// null when the polygon is valid, otherwise a user-facing reason
export const polygonIssue = (pts) => {
  if (!pts || pts.length < 3) return "Batas lahan minimal 3 titik.";
  const n = pts.length;
  for (let i = 0; i < n; i += 1) {
    for (let j = i + 1; j < n; j += 1) {
      if (j === i + 1 || (i === 0 && j === n - 1)) continue;
      if (segHit(pts[i], pts[(i + 1) % n], pts[j], pts[(j + 1) % n])) return SELF_X_MSG;
    }
  }
  if (new Set(pts.map((p) => p.join(","))).size !== n) return SELF_X_MSG;
  return polygonHectares(pts) > 0 ? null : "Luas batas lahan harus lebih dari 0.";
};
export const validHectares = (pts) => (polygonIssue(pts) ? 0 : polygonHectares(pts));
const inside = (pt, poly) => {
  let c = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i, i += 1) {
    const [yi, xi] = poly[i], [yj, xj] = poly[j];
    if ((yi > pt[0]) !== (yj > pt[0]) && pt[1] < ((xj - xi) * (pt[0] - yi)) / (yj - yi) + xi) c = !c;
  }
  return c;
};
const edgesCross = (a, b) => a.some((p, i) => b.some((q, j) => segHit(p, a[(i + 1) % a.length], q, b[(j + 1) % b.length])));
// keeps the simple sum of exclusions equal to the server's boundary∩union calculation
export const exclusionIssue = (ex, boundary, others = []) => {
  const own = polygonIssue(ex);
  if (own) return own.replace("Batas lahan", "Area Tidak Ditanami");
  if (!ex.every((p) => inside(p, boundary)) || edgesCross(ex, boundary)) return "Area Tidak Ditanami harus berada di dalam batas lahan.";
  if (others.some((o) => edgesCross(ex, o) || ex.some((p) => inside(p, o)) || o.some((p) => inside(p, ex)))) return "Area Tidak Ditanami tidak boleh bertumpuk dengan area lain.";
  return null;
};
export const centroidOf = (pts) => [Number((pts.reduce((s, p) => s + p[0], 0) / pts.length).toFixed(7)), Number((pts.reduce((s, p) => s + p[1], 0) / pts.length).toFixed(7))];

const vertexIcon = L.divIcon({ className: "", html: '<div style="width:18px;height:18px;border-radius:9999px;background:#fff;border:3px solid #facc15;box-shadow:0 1px 3px rgba(0,0,0,.4)"></div>', iconSize: [18, 18], iconAnchor: [9, 9] });

const Clicker = ({ onClick }) => {
  useMapEvents({ click: (e) => onClick([Number(e.latlng.lat.toFixed(7)), Number(e.latlng.lng.toFixed(7))]) });
  return null;
};

// Drawing mode: map is locked (no pan); a short tap adds a vertex, two fingers still zoom.
const TapDraw = ({ onTap }) => {
  const map = useMap();
  useEffect(() => {
    const el = map.getContainer();
    const pts = new Map();
    let start = null;
    let multi = false;
    map.dragging.disable();
    el.style.touchAction = "none";
    const down = (e) => {
      pts.set(e.pointerId, true);
      if (pts.size > 1) multi = true;
      if (pts.size === 1) { multi = false; start = { x: e.clientX, y: e.clientY, t: Date.now(), id: e.pointerId }; }
    };
    const up = (e) => {
      pts.delete(e.pointerId);
      const s = start;
      if (!s || s.id !== e.pointerId || multi) { if (!pts.size) start = null; return; }
      start = null;
      if (e.target.closest?.(".leaflet-marker-icon, .leaflet-control")) return;
      if (Math.hypot(e.clientX - s.x, e.clientY - s.y) > 18 || Date.now() - s.t > 900) return;
      const ll = map.mouseEventToLatLng(e);
      onTap([Number(ll.lat.toFixed(7)), Number(ll.lng.toFixed(7))]);
    };
    const cancel = (e) => { pts.delete(e.pointerId); start = null; };
    el.addEventListener("pointerdown", down);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", cancel);
    return () => {
      el.removeEventListener("pointerdown", down);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("pointercancel", cancel);
      el.style.touchAction = "";
      map.dragging.enable();
    };
  }, [map, onTap]);
  return null;
};

const FitTo = ({ target }) => {
  const map = useMap();
  useEffect(() => {
    if (!target) return;
    if (target.length === 2 && typeof target[0] === "number") map.setView(target, Math.max(map.getZoom(), 17));
    else if (target.length) map.fitBounds(target, { paddingTopLeft: [32, 56], paddingBottomRight: [56, 44], maxZoom: 19 });
  }, [target, map]);
  return null;
};

const ESRI = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer";
const tileCache = new Map();
const hasTile = async (z, lat, lng) => {
  const n = 2 ** z;
  const r = (lat * Math.PI) / 180;
  const x = Math.floor(((lng + 180) / 360) * n);
  const y = Math.floor(((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * n);
  const k = `${z}/${y}/${x}`;
  if (!tileCache.has(k)) tileCache.set(k, fetch(`${ESRI}/tilemap/${k}/1/1`).then((res) => res.json()).then((j) => !!j.data?.[0]));
  return tileCache.get(k);
};

// Finds the deepest real satellite level at the map centre; deeper zoom stretches that level instead of showing blank tiles.
const useNativeZoom = () => {
  const map = useMap();
  const [nz, setNz] = useState(17);
  useEffect(() => {
    let t;
    const check = () => {
      clearTimeout(t);
      t = setTimeout(async () => {
        const b = map.getBounds();
        const spots = [map.getCenter(), b.getNorthWest(), b.getNorthEast(), b.getSouthWest(), b.getSouthEast()];
        try {
          for (const z of [19, 18, 17, 16, 15]) {
            const ok = await Promise.all(spots.map((c) => hasTile(z, c.lat, c.lng)));
            if (ok.every(Boolean)) { setNz(z); return; }
          }
          setNz(15);
        } catch { /* keep previous level when offline */ }
      }, 300);
    };
    check();
    map.on("moveend", check);
    return () => { map.off("moveend", check); clearTimeout(t); };
  }, [map]);
  return nz;
};

const Tiles = () => {
  const nz = useNativeZoom();
  const map = useMap();
  const sat = useRef(null);
  const osm = useRef(null);
  useEffect(() => {
    const layer = sat.current;
    if (!layer || layer.options.maxNativeZoom === nz) return;
    layer.options.maxNativeZoom = nz;
    if (layer._map) layer._resetView?.();
    layer.redraw();
  }, [nz]);
  // Satellite unreachable: switch to OpenStreetMap so the map never stays blank
  useEffect(() => {
    const layer = sat.current;
    if (!layer) return undefined;
    let bad = 0, good = 0, switched = false;
    const onErr = () => {
      bad += 1;
      if (switched || bad < 4 || good > bad) return;
      switched = true;
      if (map.hasLayer(layer)) { map.removeLayer(layer); osm.current && map.addLayer(osm.current); }
      toast.warning("Citra satelit tidak dapat dimuat. Peta jalan (OpenStreetMap) ditampilkan sebagai cadangan.", { id: "sat-fallback" });
    };
    const onOk = () => { good += 1; };
    layer.on("tileerror", onErr);
    layer.on("tileload", onOk);
    return () => { layer.off("tileerror", onErr); layer.off("tileload", onOk); };
  }, [map]);
  return (
  <LayersControl position="topright">
    <LayersControl.BaseLayer checked name="Satelit">
      <TileLayer ref={sat} attribution="&copy; Esri, Maxar" url={`${ESRI}/tile/{z}/{y}/{x}`} maxNativeZoom={17} maxZoom={MAX_ZOOM} />
    </LayersControl.BaseLayer>
    <LayersControl.BaseLayer name="Peta Jalan">
      <TileLayer ref={osm} attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" maxNativeZoom={19} maxZoom={MAX_ZOOM} />
    </LayersControl.BaseLayer>
  </LayersControl>
  );
};

const fmtM = (m) => `${m.toLocaleString("id-ID", { maximumFractionDigits: 1 })} m`;
const edgesOf = (pts) => {
  if (!pts || pts.length < 2) return [];
  const count = pts.length >= 3 ? pts.length : 1;
  return Array.from({ length: count }, (_, i) => {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    return { i, a, b, m: L.latLng(a).distanceTo(L.latLng(b)), mid: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2] };
  });
};

const EdgeLabels = ({ edges }) => {
  const map = useMap();
  const [, setZ] = useState(map.getZoom());
  useMapEvents({ zoomend: () => setZ(map.getZoom()) });
  const px = (e) => map.latLngToContainerPoint(e.a).distanceTo(map.latLngToContainerPoint(e.b));
  return edges.filter((e) => px(e) >= 56).map((e) => (
  <CircleMarker key={`${e.i}-${e.mid[0]}-${e.mid[1]}`} center={e.mid} radius={0} interactive={false} pathOptions={{ opacity: 0, fillOpacity: 0 }}>
    <Tooltip permanent direction="center" className="edge-label">{fmtM(e.m)}</Tooltip>
  </CircleMarker>
));
};

const DRAFT = "sf-map-draft:";
export const clearMapDraft = (key) => { if (key) localStorage.removeItem(DRAFT + key); };
const readDraft = (key, current) => {
  if (!key) return null;
  try {
    const d = JSON.parse(localStorage.getItem(DRAFT + key));
    return d?.polygon?.length && JSON.stringify(d.polygon) !== JSON.stringify(current || []) ? d : null;
  } catch { return null; }
};

const DraftBanner = ({ draft, label, onRestore, onDiscard, testid }) => (
  <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid={`${testid}-draft-banner`}>
    <span className="flex-1 min-w-[200px]">Ada draf {label.toLowerCase()} tersimpan di perangkat ini: <b>{draft.polygon.length} titik</b> · {new Date(draft.ts).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}</span>
    <button type="button" onClick={onRestore} className="h-8 px-3 rounded-full bg-emerald-800 text-white text-xs font-semibold" data-testid={`${testid}-draft-restore`}>Pulihkan</button>
    <button type="button" onClick={onDiscard} className="h-8 px-3 rounded-full border border-amber-400 text-xs font-semibold" data-testid={`${testid}-draft-discard`}>Buang</button>
  </div>
);

const Overlays = ({ overlays = [] }) => overlays.map((o, i) => o.points?.length >= 3 && (
  <Polygon key={i} positions={o.points} pathOptions={{ color: o.color || "#94a3b8", weight: 2, fillOpacity: o.fill ?? 0.15, dashArray: o.dashed ? "6 4" : undefined }}>
    {o.label && <Tooltip sticky>{o.label}</Tooltip>}
  </Polygon>
));

export const Legend = ({ testid = "map-legend", keys = Object.keys(STATE_COLORS), exclusion }) => (
  <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs" data-testid={testid}>
    {keys.map((k) => <span key={k} className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm" style={{ background: STATE_COLORS[k][0] }} />{STATE_COLORS[k][1]}</span>)}
    {exclusion && <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm border-2" style={{ borderColor: EXCLUSION_COLOR }} />Tidak dapat ditanami</span>}
  </div>
);

const numIcon = (label, color) => L.divIcon({ className: "", iconSize: [24, 24], iconAnchor: [12, 12],
  html: `<div style="background:${color};color:#fff;border:2px solid #fff;border-radius:9999px;width:24px;height:24px;display:flex;align-items:center;justify-content:center;font:700 11px sans-serif;box-shadow:0 1px 4px rgba(0,0,0,.5)">${label}</div>` });

export const LandMapView = ({ point, overlays, height = "h-72", testid, markers = [] }) => {
  const all = overlays?.flatMap((o) => o.points || []) || [];
  const fit = useMemo(() => (all.length >= 3 ? all : point), [JSON.stringify(all), point?.[0]]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className={`${height} w-full rounded-xl overflow-hidden border border-slate-200 relative z-0`} data-testid={testid}>
      <MapContainer center={point || DEFAULT_CENTER} zoom={point ? 16 : 8} maxZoom={MAX_ZOOM} className="h-full w-full" scrollWheelZoom={false}>
        <Tiles />
        <Overlays overlays={overlays} />
        {point && <CircleMarker center={point} radius={7} pathOptions={{ color: "#fff", weight: 2, fillColor: "#047857", fillOpacity: 1 }} />}
        {markers.map((mk) => (
          <Marker key={mk.key} position={[mk.lat, mk.lng]} icon={numIcon(mk.label, mk.color || "#b45309")} eventHandlers={{ click: () => mk.onClick?.() }}>
            {mk.title && <Tooltip>{mk.title}</Tooltip>}
          </Marker>
        ))}
        <FitTo target={fit} />
      </MapContainer>
    </div>
  );
};

const ToolBtn = ({ active, onClick, children, testid, disabled }) => (
  <button type="button" disabled={disabled} onClick={onClick} data-testid={testid}
    className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-sm font-semibold border transition-colors disabled:opacity-40 ${active ? "bg-emerald-800 text-white border-emerald-800" : "bg-white text-slate-700 border-slate-200 hover:border-emerald-600"}`}>
    {children}
  </button>
);

const PlaceSearch = ({ onFound, testid }) => {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (q.trim().length < 3) return;
    setBusy(true);
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=id&q=${encodeURIComponent(q)}`);
      const [hit] = await r.json();
      if (hit) { onFound([Number(hit.lat), Number(hit.lon)]); toast.success(hit.display_name.slice(0, 80)); } else toast.error("Lokasi tidak ditemukan");
    } catch { toast.error("Pencarian lokasi gagal"); } finally { setBusy(false); }
  };
  return (
    <div className="flex gap-2 w-full sm:w-auto sm:min-w-[320px]">
      <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); go(); } }}
        placeholder="Cari nagari, desa, kecamatan..." className="flex-1 h-9 rounded-full border border-slate-200 px-3 text-sm" data-testid={`${testid}-search-input`} />
      <ToolBtn onClick={go} testid={`${testid}-search-btn`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}Cari</ToolBtn>
    </div>
  );
};

const COORD_RE = /^\s*([+-]?\d{1,3}(?:\.\d+)?)\s*,\s*([+-]?\d{1,3}(?:\.\d+)?)\s*$/;
export const parseCoords = (s) => {
  const m = COORD_RE.exec(s || "");
  if (!m) return { error: "Format salah. Tulis: latitude, longitude — contoh -0.228451, 100.631245" };
  const lat = Number(m[1]), lng = Number(m[2]);
  if (lat < -90 || lat > 90) return { error: "Latitude harus antara -90 sampai 90." };
  if (lng < -180 || lng > 180) return { error: "Longitude harus antara -180 sampai 180." };
  return { ll: [Number(lat.toFixed(7)), Number(lng.toFixed(7))] };
};

const NUM = "([+-]?\\d{1,3}(?:\\.\\d+)?)";
const LINK_PATTERNS = [
  new RegExp(`!3d${NUM}!4d${NUM}`),
  new RegExp(`[?&](?:q|query|ll|center|destination|daddr|sll)=(?:loc:)?${NUM},\\s*${NUM}`),
  new RegExp(`@${NUM},${NUM}`),
  new RegExp(`/(?:place|search|dir)/${NUM},\\s*${NUM}`),
];
const isLink = (s) => /^\s*(https?:\/\/|www\.|maps\.app\.goo\.gl|goo\.gl|maps\.google)/i.test(s || "");
export const coordsFromLink = (url) => {
  let s = url || "";
  try { s = decodeURIComponent(s.replace(/\+/g, " ")); } catch { /* keep raw */ }
  for (const re of LINK_PATTERNS) {
    const m = re.exec(s);
    if (m) return parseCoords(`${m[1]},${m[2]}`);
  }
  return null;
};
const LINK_ERR = "Koordinat tidak dapat dibaca dari link Google Maps. Silakan masukkan latitude, longitude secara langsung.";

const CoordSearch = ({ onFound, testid }) => {
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const done = (r) => {
    setErr(r.error || "");
    if (r.ll) { onFound(r.ll); toast.success(`Peta dipindah ke ${r.ll[0]}, ${r.ll[1]}`); }
  };
  const fromLink = async () => {
    const url = /^https?:\/\//i.test(q.trim()) ? q.trim() : `https://${q.trim()}`;
    const direct = coordsFromLink(url);
    if (direct) return done(direct);
    setBusy(true);
    try {
      const { data } = await api.get("/lands/maps-link", { params: { url } });
      done(data.lat != null ? parseCoords(`${data.lat},${data.lng}`) : coordsFromLink(data.url) || { error: LINK_ERR });
    } catch (e) {
      done({ error: e.response?.status === 400 ? "Tautan bukan tautan Google Maps. Silakan masukkan latitude, longitude secara langsung." : "Tautan Google Maps tidak dapat dibuka. Periksa koneksi, atau masukkan latitude, longitude secara langsung." });
    } finally { setBusy(false); }
  };
  const go = () => { if (busy) return; if (isLink(q)) fromLink(); else done(parseCoords(q)); };
  return (
    <div className="w-full sm:w-auto sm:min-w-[320px]">
      <div className="flex flex-col sm:flex-row gap-2">
        <input value={q} inputMode="text" autoComplete="off" onChange={(e) => { setQ(e.target.value); setErr(""); }} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); go(); } }}
          placeholder="-0.228451, 100.631245 atau tautan Google Maps" aria-invalid={!!err} className={`shrink-0 sm:flex-1 min-w-0 h-9 rounded-full border px-3 text-sm font-mono ${err ? "border-red-400" : "border-slate-200"}`} data-testid={`${testid}-coord-input`} />
        <ToolBtn onClick={go} disabled={busy} testid={`${testid}-coord-btn`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crosshair className="h-4 w-4" />}Cari Koordinat</ToolBtn>
      </div>
      {err && <p className="mt-1 text-xs text-red-600" role="alert" data-testid={`${testid}-coord-error`}>{err}</p>}
    </div>
  );
};

const SearchBox = ({ onPlace, onCoord, testid }) => {
  const [tab, setTab] = useState("place");
  const tabCls = (t) => `h-7 px-3 rounded-full text-xs font-semibold transition-colors ${tab === t ? "bg-emerald-800 text-white" : "text-slate-600 hover:text-emerald-800"}`;
  return (
    <div className="space-y-2">
      <div className="inline-flex rounded-full border border-slate-200 bg-white p-0.5" role="tablist">
        <button type="button" role="tab" aria-selected={tab === "place"} onClick={() => setTab("place")} className={tabCls("place")} data-testid={`${testid}-tab-place`}>Cari Nama Daerah</button>
        <button type="button" role="tab" aria-selected={tab === "coord"} onClick={() => setTab("coord")} className={tabCls("coord")} data-testid={`${testid}-tab-coord`}>Cari Koordinat</button>
      </div>
      {tab === "place" ? <PlaceSearch onFound={onPlace} testid={testid} /> : <CoordSearch onFound={onCoord} testid={testid} />}
    </div>
  );
};

export const LandMapEditor = ({ point, polygon, onPoint, onPolygon: emitPolygon, overlays, allowPoint = true, polygonLabel = "Batas Lahan", testid = "land-map", draftKey }) => {
  const [draft, setDraft] = useState(() => readDraft(draftKey, polygon));
  const onPolygon = (p) => {
    emitPolygon(p);
    if (!draftKey) return;
    if (p.length) localStorage.setItem(DRAFT + draftKey, JSON.stringify({ polygon: p, point, ts: Date.now() }));
    else clearMapDraft(draftKey);
  };
  const restore = () => {
    emitPolygon(draft.polygon);
    if (draft.point && onPoint && !point) onPoint(draft.point);
    setFit(draft.polygon);
    setDraft(null);
  };
  const [mode, setMode] = useState(allowPoint && !point ? "point" : "polygon");
  const [eraser, setEraser] = useState(false);
  const [pan, setPan] = useState(false);
  const [fit, setFit] = useState(polygon?.length >= 3 ? polygon : point || overlays?.flatMap((o) => o.points || []) || null);
  const [found, setFound] = useState(null);
  const goCoord = (ll) => {
    setFound(ll);
    if (allowPoint && onPoint) onPoint(ll);
    setFit([...ll]);
  };
  const click = (ll) => {
    if (eraser) return;
    if (mode === "point") { onPoint?.(ll); if (!point) setFit(ll); } else onPolygon([...(polygon || []), ll]);
  };
  const gps = () => {
    if (!navigator.geolocation) { toast.error("Perangkat tidak mendukung GPS"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const ll = [Number(pos.coords.latitude.toFixed(7)), Number(pos.coords.longitude.toFixed(7))];
        if (mode === "point" && allowPoint) onPoint(ll); else onPolygon([...(polygon || []), ll]);
        setFit(ll);
        toast.success(`Lokasi GPS didapat (akurasi ±${Math.round(pos.coords.accuracy)} m)`);
      },
      () => toast.error("Tidak bisa membaca GPS. Izinkan akses lokasi di browser."),
      { enableHighAccuracy: true, timeout: 15000 },
    );
  };
  const drawing = mode === "polygon" && !eraser && !pan;
  const addVertex = useCallback((ll) => onPolygon([...(polygon || []), ll]), [onPolygon, polygon]);
  const pick = (m) => { setMode(m); setEraser(false); setPan(false); };
  const move = (i, ll) => onPolygon(polygon.map((p, j) => (j === i ? [Number(ll.lat.toFixed(7)), Number(ll.lng.toFixed(7))] : p)));
  const issue = polygon?.length >= 3 ? polygonIssue(polygon) : null;
  const ha = issue ? 0 : polygonHectares(polygon);
  const edges = edgesOf(polygon);
  const perimeter = edges.reduce((s, e) => s + e.m, 0);
  return (
    <div className="space-y-2" data-testid={testid}>
      {draft && <DraftBanner draft={draft} label={polygonLabel} testid={testid} onRestore={restore} onDiscard={() => { clearMapDraft(draftKey); setDraft(null); }} />}
      <SearchBox onPlace={(ll) => setFit(ll)} onCoord={goCoord} testid={testid} />
      <div className="flex flex-wrap gap-2">
        {allowPoint && <ToolBtn active={mode === "point" && !eraser} onClick={() => pick("point")} testid={`${testid}-mode-point`}><MapPin className="h-4 w-4" />Titik Lokasi</ToolBtn>}
        <ToolBtn active={drawing} onClick={() => pick("polygon")} testid={`${testid}-mode-polygon`}><Spline className="h-4 w-4" />{polygonLabel}</ToolBtn>
        {mode === "polygon" && <ToolBtn active={pan} onClick={() => { setPan(!pan); setEraser(false); }} testid={`${testid}-pan`}><Hand className="h-4 w-4" />Geser Peta</ToolBtn>}
        <ToolBtn onClick={gps} testid={`${testid}-gps`}><Crosshair className="h-4 w-4" />GPS Saya</ToolBtn>
        <ToolBtn disabled={!polygon?.length} onClick={() => onPolygon(polygon.slice(0, -1))} testid={`${testid}-undo`}><Undo2 className="h-4 w-4" />Undo</ToolBtn>
        <ToolBtn active={eraser} disabled={!polygon?.length} onClick={() => { setEraser(!eraser); setPan(false); }} testid={`${testid}-erase`}><Eraser className="h-4 w-4" />Hapus Titik</ToolBtn>
        <ToolBtn disabled={!polygon?.length} onClick={() => onPolygon([])} testid={`${testid}-clear`}><Trash2 className="h-4 w-4" />Ulangi</ToolBtn>
      </div>
      <p className={`text-xs ${drawing ? "text-emerald-800 font-semibold" : "text-slate-500"}`} data-testid={`${testid}-hint`}>{eraser ? "Ketuk titik kuning yang ingin dihapus." : mode === "point" ? "Ketuk peta untuk menaruh titik lokasi lahan." : pan ? "Mode geser: peta bisa digeser. Tekan Geser Peta lagi untuk lanjut menggambar." : `Peta dikunci saat menggambar. Ketuk berurutan mengelilingi ${polygonLabel.toLowerCase()}; zoom pakai 2 jari atau tombol +/−. Geser titik kuning untuk mengedit.`}</p>
      <div className="h-80 sm:h-[28rem] w-full rounded-xl overflow-hidden border border-slate-200 relative z-0" data-testid={`${testid}-canvas`}>
        <MapContainer center={point || polygon?.[0] || DEFAULT_CENTER} zoom={point || polygon?.length ? 18 : 8} maxZoom={MAX_ZOOM} className="h-full w-full">
          <Tiles />
          {drawing ? <TapDraw onTap={addVertex} /> : !pan && mode === "point" && <Clicker onClick={click} />}
          <Overlays overlays={overlays} />
          {polygon?.length >= 3 && <Polygon positions={polygon} pathOptions={{ color: issue ? "#dc2626" : "#facc15", weight: 3, fillOpacity: 0.25 }} />}
          {polygon?.length === 2 && <Polyline positions={polygon} pathOptions={{ color: "#facc15", weight: 3 }} />}
          {polygon?.map((p, i) => (
            <Marker key={`${i}-${p[0]}-${p[1]}`} position={p} icon={vertexIcon} draggable={!eraser}
              eventHandlers={{ dragend: (e) => move(i, e.target.getLatLng()), click: () => eraser && onPolygon(polygon.filter((_, j) => j !== i)) }} />
          ))}
          {point && <CircleMarker center={point} radius={8} pathOptions={{ color: "#fff", weight: 2, fillColor: "#047857", fillOpacity: 1 }} />}
          {found && !(allowPoint && point) && <CircleMarker center={found} radius={8} interactive={false} pathOptions={{ color: "#fff", weight: 2, fillColor: "#dc2626", fillOpacity: 1 }} />}
          <EdgeLabels edges={edges} />
          <FitTo target={fit} />
        </MapContainer>
      </div>
      {edges.length > 0 && (
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-700" data-testid={`${testid}-edges`}>
          <p className="font-semibold text-slate-900">Panjang sisi{perimeter > 0 && edges.length > 1 && ` · keliling ${fmtM(perimeter)}`}</p>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 font-mono">
            {edges.map((e) => <span key={e.i} data-testid={`${testid}-edge-${e.i}`}>Titik {e.i + 1}→{(e.i + 1) % polygon.length + 1}: {fmtM(e.m)}</span>)}
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600 font-mono">
        {allowPoint && <span data-testid={`${testid}-coords`}>Titik: {point ? `${point[0]}, ${point[1]}` : "belum dipilih"}</span>}
        <span data-testid={`${testid}-vertices`}>{polygonLabel}: {polygon?.length || 0} titik{ha > 0 && ` · ${fmtArea(ha)}`}</span>
      </div>
      {issue && <p className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700" role="alert" data-testid={`${testid}-polygon-error`}>{polygonLabel === "Batas Lahan" ? issue : issue.replace("Batas lahan", polygonLabel)}</p>}
    </div>
  );
};
